import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Home, X, MapPin, Eye, Box, Trash2 } from 'lucide-react';
import { listMyDesigns, deleteDesign } from './designsApi.jsx';
import { shortViews } from './listingFormat.jsx';
import LoopVideo, { filmSrc } from './loopVideo.jsx';
import './myDesigns.scss';

const DesignWalk = lazy(() => import('./designWalk.jsx'));

// طرح‌های ذخیره‌شده‌ی خودِ کاربر در پروفایل: هر طرح با فیلمِ کوتاهش (پهپادی + حرکت بینِ اتاق‌ها، بی‌صدا و
// تکرارشونده) در شبکه‌ی «فایل‌ها»؛ طرح‌های قدیمیِ بدونِ فیلم با عکس. لمس = پیش‌نمایش (نمایشِ سه‌بعدیِ کامل
// قدمِ بعدی است).

const fa = (n) => Number(n).toLocaleString('fa-IR');
const DEAL = { sale: 'فروش', rent: 'رهن و اجاره' };
const PROPERTY = { apartment: 'آپارتمان', villa: 'ویلایی', land: 'زمین', office: 'اداری', shop: 'مغازه' };

export function useMyDesigns(user) {
  const [state, setState] = useState({ items: [], loading: Boolean(user), error: '' });
  useEffect(() => {
    if (!user) {
      setState({ items: [], loading: false, error: '' });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: '' }));
    listMyDesigns()
      .then((r) => !cancelled && setState({ items: r.items || [], loading: false, error: '' }))
      .catch((e) => !cancelled && setState({ items: [], loading: false, error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [user && user.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // بعد از حذف، از فهرست هم برداشته شود
  const remove = (id) => setState((s) => ({ ...s, items: s.items.filter((d) => d.id !== id) }));
  return { ...state, remove };
}

// پرسیدن قبل از حذف (برگشت‌ناپذیر از دیدِ کاربر)
function DeleteAsk({ design, onCancel, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    setBusy(true);
    setError('');
    try {
      await deleteDesign(design.id);
      onDeleted(design.id);
    } catch (e) {
      setError(e.message || 'حذف نشد — دوباره تلاش کنید.');
      setBusy(false);
    }
  };
  return (
    <div className="my-designs-ask" onClick={busy ? undefined : onCancel}>
      <div className="my-designs-ask__box" onClick={(e) => e.stopPropagation()}>
        <span className="my-designs-ask__icon">
          <Trash2 size={22} strokeWidth={2} />
        </span>
        <strong>این {design.listing ? 'آگهی' : 'طرح'} حذف شود؟</strong>
        <p>از پروفایل، اکسپلور و جست‌وجو برداشته می‌شود و دیگر کسی آن را نمی‌بیند.</p>
        {error && <p className="my-designs-ask__error">{error}</p>}
        <div className="my-designs-ask__actions">
          <button type="button" className="is-danger" onClick={go} disabled={busy}>
            {busy ? 'در حالِ حذف…' : 'حذف'}
          </button>
          <button type="button" onClick={onCancel} disabled={busy}>
            انصراف
          </button>
        </div>
      </div>
    </div>
  );
}

function priceText(l) {
  if (!l) return '';
  if (l.dealType === 'rent') {
    const parts = [];
    if (l.deposit) parts.push(`ودیعه ${fa(l.deposit)}`);
    if (l.rent) parts.push(`اجاره ${fa(l.rent)}`);
    return parts.length ? `${parts.join(' - ')} تومان` : '';
  }
  return l.price ? `${fa(l.price)} تومان` : '';
}

function DesignPreview({ design, onClose, onDelete }) {
  const l = design.listing;
  const [walk, setWalk] = useState(false);
  const meta = [
    l?.area && `${fa(l.area)} متر`,
    l?.rooms && `${fa(l.rooms)} اتاق`,
    l?.floor != null && l?.floor !== '' && `طبقه‌ی ${fa(l.floor)}${l.totalFloors ? ` از ${fa(l.totalFloors)}` : ''}`,
    l?.buildYear && `ساخت ${fa(l.buildYear)}`,
  ].filter(Boolean);
  return (
    <div className="my-designs-preview" onClick={onClose}>
      <div className="my-designs-preview__card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="my-designs-preview__close" onClick={onClose} aria-label="بستن">
          <X size={18} strokeWidth={2} />
        </button>
        {design.filmUrl ? (
          <LoopVideo src={filmSrc(design)} poster={design.thumbUrl} className="my-designs-preview__film" />
        ) : (
          design.thumbUrl && <img src={design.thumbUrl} alt="" className="my-designs-preview__img" />
        )}
        <div className="my-designs-preview__body">
          {design.title && <span className="my-designs-preview__private">{design.title} · فقط برای شما</span>}
          <h3>{l?.title || 'طرحِ بدونِ آگهی'}</h3>
          {l && (
            <p className="my-designs-preview__tags">
              {[DEAL[l.dealType], PROPERTY[l.propertyType]].filter(Boolean).join(' · ')}
            </p>
          )}
          {priceText(l) && <p className="my-designs-preview__price">{priceText(l)}</p>}
          {meta.length > 0 && <p className="my-designs-preview__meta">{meta.join(' • ')}</p>}
          {l?.site && (
            <p className="my-designs-preview__meta">
              <MapPin size={13} strokeWidth={2} /> لوکیشن ثبت شده
            </p>
          )}
          <p className="my-designs-preview__foot">
            <span>
              <Eye size={13} strokeWidth={2} /> {fa(design.views || 0)} بازدید
            </span>
            <span>{new Date(design.createdAt).toLocaleDateString('fa-IR')}</span>
          </p>
          {design.modelUrl && design.lightmapUrl && design.planUrl && (
            <button type="button" className="my-designs-preview__enter" onClick={() => setWalk(true)}>
              <Box size={17} strokeWidth={1.9} />
              <span>ورود به خانه</span>
            </button>
          )}
          {onDelete && design.isOwner && (
            <button type="button" className="my-designs-preview__delete" onClick={() => onDelete(design)}>
              <Trash2 size={16} strokeWidth={1.9} />
              <span>حذفِ {design.listing ? 'آگهی' : 'طرح'}</span>
            </button>
          )}
        </div>
      </div>
      {walk && (
        <div onClick={(e) => e.stopPropagation()}>
          <Suspense fallback={null}>
            <DesignWalk design={design} onClose={() => setWalk(false)} />
          </Suspense>
        </div>
      )}
    </div>
  );
}

// filter: 'all' | 'sale' | 'rent'
// onDeleted(id): اگر داده شود، طرح‌های خودِ کاربر دکمه‌ی حذف دارند
export function DesignsGrid({ designs, loading, error, filter = 'all', className, emptyText, onDeleted }) {
  const [open, setOpen] = useState(null);
  const [ask, setAsk] = useState(null);
  const items = filter === 'all' ? designs : designs.filter((d) => d.listing?.dealType === filter);

  if (loading) {
    return (
      <section className={className}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="my-designs__cell is-loading" />
        ))}
      </section>
    );
  }
  if (error) return <p className="my-designs__empty">{error}</p>;
  if (!items.length) {
    return (
      <div className="my-designs__empty">
        <Home size={30} strokeWidth={1.3} />
        <p>{emptyText || 'هنوز طرحی ذخیره نکرده‌اید. بعد از رندر، «ارسال فایل روی سرور» را بزنید.'}</p>
      </div>
    );
  }
  return (
    <>
      <section className={className}>
        {items.map((d) => (
          <div key={d.id} className="my-designs__item">
            <button type="button" className="my-designs__cell" onClick={() => setOpen(d)}>
              {d.filmUrl ? (
                <LoopVideo src={filmSrc(d, true)} poster={d.thumbUrl} />
              ) : d.thumbUrl ? (
                <img src={d.thumbUrl} alt="" loading="lazy" />
              ) : (
                <Home size={26} strokeWidth={1.4} />
              )}
              {d.listing?.area && <span className="my-designs__badge">{fa(d.listing.area)} متر</span>}
              <span className="my-designs__views">
                <Eye size={11} strokeWidth={2.2} />
                {shortViews(d.views)}
              </span>
            </button>
            {onDeleted && d.isOwner && (
              <button type="button" className="my-designs__delete" onClick={() => setAsk(d)} aria-label="حذف">
                <Trash2 size={14} strokeWidth={2} />
              </button>
            )}
          </div>
        ))}
      </section>
      {open && <DesignPreview design={open} onClose={() => setOpen(null)} onDelete={onDeleted ? setAsk : null} />}
      {ask && (
        <DeleteAsk
          design={ask}
          onCancel={() => setAsk(null)}
          onDeleted={(id) => {
            setAsk(null);
            setOpen(null);
            onDeleted(id);
          }}
        />
      )}
    </>
  );
}
